'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import ShowingTagCover from '@/app/[locale]/[domain]/(default-layout)/specials/_components/ShowingTagCover';

import CardWrapper from '@/components/CardWrapper.tsx';
import BellSubscribeButton from '@/components/newsletter/BellSubscribeButton';
import SkeletonView from '@/components/skeleton-view/SkeletonView';
import TicketShape from '@/components/ticket-shape/TicketShape';

import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { useFutureShowings } from '@/hooks/useFutureShowings';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

export default function SpecialsSectionItem(props: { showingTag: ShowingTagDto }): React.JSX.Element {
    const { showingTag } = props;
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();

    const t = useTranslations();

    const cinema = domainConfiguration.cinema;
    const cinemaId = cinema.id;

    const { data: showingsResponse } = useFutureShowings(cinemaId, null, {
        showingTagIds: [showingTag.id]
    });

    return (
        <div className="relative w-[325px] h-[308px] rounded-8 overflow-hidden flex-none flex flex-col group border-2 border-[var(--surface-color)]">
            <BellSubscribeButton
                topic={{
                    type: 'showing_tag',
                    referenceId: String(showingTag.id),
                    label: showingTag.name
                }}
                className="absolute top-8 right-8"
            />
            <div className="w-full h-auto rounded-4 overflow-hidden">
                <ShowingTagCover
                    showingTag={showingTag}
                    showingsCount={showingsResponse?.length}
                />
            </div>
            <CardWrapper className="flex-auto w-full flex flex-col space-y-0 px-10 pb-10 justify-between">
                <div className="w-full flex flex-col space-y-4 py-6 flex-auto justify-center">
                    <span className="text-body-bold-16 whitespace-nowrap text-ellipsis truncate">
                        {showingTag.name}
                    </span>
                    <div className="flex flex-row space-x-4 items-start gradient-mask-r-90 overflow-x-auto no-scrollbar">
                        {showingsResponse ? (
                            <span
                                className="text-body-bold-12"
                                style={
                                    domainConfiguration
                                        ? {
                                              color:
                                                  domainConfiguration.style.secondaryColor ??
                                                  domainConfiguration.style.primaryColor
                                          }
                                        : undefined
                                }>
                                {t('specials.n-showings', { count: showingsResponse?.length ?? 0 })}
                            </span>
                        ) : (
                            <SkeletonView customHeight="h-18" />
                        )}
                    </div>
                </div>
                <Link
                    className="!h-48 clickable-element"
                    href={
                        {
                            pathname: '/specials/[showingTagId]',
                            params: {
                                showingTagId: showingTag.id
                            }
                        } as HrefOrHrefWithParams<string>
                    }
                    onClick={() =>
                        track('Special Item Clicked', {
                            showingTagId: showingTag.id,
                            name: showingTag.name
                        })
                    }
                    aria-label={showingTag.name}
                    prefetch={false}>
                    <TicketShape
                        config={{
                            fillColorHex: domainConfiguration.style.surfaceColor,
                            fillColorOpacity: 1,
                            cornerRadius: 8
                        }}>
                        <div className="w-full h-full flex items-center justify-center relative text-[var(--on-surface-color)]">
                            <span className="text-body-bold-14">{t('specials.go-to-screenings')}</span>
                        </div>
                    </TicketShape>
                </Link>
            </CardWrapper>
        </div>
    );
}
